import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { priceConstructor } from "../../utils/general";
import { getShopifyProductSliderImage } from "../../utils/shop";

// Stylesheets
import "./ShopifyProductSlider.scss";

// Components
import Paragraph from "../Atoms/Paragraph";
import Link from "../Atoms/Link";
import { ImgixImage } from "../Utils/ImgixImage";
import LoadMoreButton from "../Utils/LoadMoreButton";
import { Mobile, TabletOrDesktop } from "../Utils/ResponsiveComponent";
import { TrackingContext } from "../Utils/TrackingWrapper";

// Default to 3 products per row (applies to tablet & desktop)
const PRODUCT_COUNT_PER_ROW = 3;

// Show 2 rows on tablet & desktop
const INCREMENTAL_PRODUCT_COUNT = PRODUCT_COUNT_PER_ROW * 2;

export const handleShowMoreClick =
  ({ setMaxProductsShowing, maxProductsShowing }) =>
  () => {
    setMaxProductsShowing(maxProductsShowing + INCREMENTAL_PRODUCT_COUNT);
  };

export const ShopifyProductSliderItem = ({
  productData,
  itemListName,
  isLink,
  children,
}) => {
  // GTM Track - View Item List
  const {
    trackingActions: { trackSelectItem },
  } = useContext(TrackingContext);

  const onItemClick = () => {
    trackSelectItem({ itemListName, product: productData });
  };

  const { src, altText } = getShopifyProductSliderImage(productData.images);
  const component = (
    <div
      className="inner"
      data-testid="product_slider_item"
      onClick={onItemClick}
    >
      <div className="picture_wrapper">
        <ImgixImage
          src={src}
          className="slider_image"
          alt={altText || productData.productName}
          isShopify
          htmlAttributes={{ loading: "lazy" }}
          sizes={{ desktop: "368px", default: "255px" }}
        />
      </div>

      <div className="main">
        <header className="header">
          <h4 className="title h5_text">{productData.productName}</h4>
          <span className="price body_primary">
            {priceConstructor({ price: productData.price })}
          </span>
        </header>

        <Paragraph className="editorial_copy">
          {productData.editorial}
        </Paragraph>
      </div>

      {children}
    </div>
  );

  if (isLink) {
    return (
      <Link to={productData.url} className="product">
        {component}
      </Link>
    );
  }

  return <div className="product">{component}</div>;
};

ShopifyProductSliderItem.propTypes = {
  children: PropTypes.any,
  isLink: PropTypes.bool,
  productData: PropTypes.shape({
    url: PropTypes.string.isRequired,
    productName: PropTypes.string.isRequired,
    variantId: PropTypes.string,
    variants: PropTypes.arrayOf(PropTypes.object),
    images: PropTypes.arrayOf(PropTypes.object).isRequired,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }),
};

ShopifyProductSliderItem.defaultProps = {
  isLink: true,
};

export const ShopifyProductSlider = ({
  className,
  productList,
  productLimit,
  renderItems,
  itemListName,
}) => {
  // GTM Track - View Item List
  const {
    trackingActions: { trackViewItemList },
  } = useContext(TrackingContext);

  // Leave out products that don't have images & products missing a silo image
  const productsList = productList.filter(
    (product) =>
      product.images.length > 0 && getShopifyProductSliderImage(product.images)
  );

  // First slice products to the amount specified as the product limit
  const products = productsList.slice(0, productLimit);
  const productCount = products.length;

  // On mobile, we show all items up to the product limit
  // since we don't "paginate"
  const mobileProductCount = productCount;

  // On tablet & up, we default to the product limit as well, but if there
  // are more than 6 items, then we only show the first 6 to start
  // since we "paginate"
  let tabletAndDesktopProductCount = productCount;

  if (productCount > INCREMENTAL_PRODUCT_COUNT) {
    tabletAndDesktopProductCount = INCREMENTAL_PRODUCT_COUNT;
  }

  // Set the max number of products currently showing. This is used for tablet
  // and desktop layouts, for "show more" functionality.
  // Note: the max number will be the actual number showing, except if
  // there is less than the full number of results (i.e. less than 6) for
  // the last set of paginated products.
  const [maxProductsShowing, setMaxProductsShowing] = useState(
    tabletAndDesktopProductCount
  );

  // Limit product data to the number we'll show
  // Mobile: non-paginated
  const mobileProducts = products.slice(0, mobileProductCount);

  // Tablet & desktop: paginated when there are > 6
  const tabletAndDesktopProducts = products.slice(0, maxProductsShowing);

  useEffect(() => {
    trackViewItemList({ itemListName, productList });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={classNames("shopify_product_slider", className)}
      data-testid="product_slider"
    >
      <Mobile>
        {mobileProducts.map((productData, idx) => {
          return renderItems({ productData, index: idx });
        })}
      </Mobile>

      <TabletOrDesktop>
        {tabletAndDesktopProducts.map((productData, idx) => {
          return renderItems({ productData, index: idx, itemListName });
        })}

        <LoadMoreButton
          className="show_more_button"
          shouldRender={productLimit > 3 && maxProductsShowing < productCount}
          onClick={handleShowMoreClick({
            setMaxProductsShowing,
            maxProductsShowing,
          })}
        >
          Show More
        </LoadMoreButton>
      </TabletOrDesktop>
    </div>
  );
};

ShopifyProductSlider.propTypes = {
  className: PropTypes.any,
  children: PropTypes.any,
  renderItems: PropTypes.func.isRequired,
  productList: PropTypes.arrayOf(PropTypes.object).isRequired,
  productLimit: PropTypes.number,
  itemListName: PropTypes.string,
};

ShopifyProductSlider.defaultProps = {
  productLimit: 3,
};

export default ShopifyProductSlider;
